import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { ArrowRight, CalendarCheck, MessageCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonVariants } from '@/components/ui/button'
import { SectionHeading } from '@/components/generals/section-heading'
import { BookButton } from '@/components/landing/booking-provider'

import {
    otherServices,
    serviceOrder,
    services,
    whatsappLink
} from '@/constants/clinic'

const ServicesSection: FC = (): ReactNode => {
    return (
        <section id='layanan' aria-labelledby='layanan-title' className='py-20 sm:py-24'>
            <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
                <SectionHeading
                    id='layanan-title'
                    eyebrow='Layanan'
                    title='Layanan kami'
                    description='Poli rawat jalan dapat dijanjikan secara online. Layanan lain cukup datang langsung atau hubungi klinik.'
                />

                <div className='mt-12 flex items-center gap-3'>
                    <h3 className='text-lg font-bold text-ink'>
                        Bisa dijanjikan online
                    </h3>

                    <span className='inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand-700 ring-1 ring-brand-100 ring-inset'>
                        <CalendarCheck className='size-3.5' aria-hidden='true' />
                        3 poli
                    </span>
                </div>

                <div className='mt-6 grid gap-6 md:grid-cols-3'>
                    {serviceOrder.map(id => {
                        const item = services[id]
                        const Icon = item.icon

                        return (
                            <article
                                key={id}
                                className='group flex flex-col overflow-hidden rounded-3xl bg-white shadow-sm ring-1 ring-border transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-brand-950/10 motion-reduce:hover:translate-y-0'
                            >
                                <div className='relative aspect-[16/10] overflow-hidden bg-brand-50'>
                                    <Image
                                        src={item.image}
                                        alt={item.imageAlt}
                                        fill
                                        sizes='(min-width: 1280px) 400px, (min-width: 768px) 33vw, 100vw'
                                        className='object-cover transition-transform duration-500 group-hover:scale-105 motion-reduce:group-hover:scale-100'
                                    />

                                    <span className='absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-brand-800 shadow-sm backdrop-blur'>
                                        <CalendarCheck className='size-3.5' aria-hidden='true' />
                                        Janji online
                                    </span>
                                </div>

                                <div className='flex flex-1 flex-col p-6'>
                                    <span className='relative -mt-12 mb-4 flex size-14 items-center justify-center rounded-2xl bg-white text-brand-700 shadow-lg shadow-brand-950/10 ring-1 ring-border'>
                                        <Icon className='size-6' aria-hidden='true' />
                                    </span>

                                    <h4 className='text-xl font-bold text-ink'>
                                        {item.name}
                                    </h4>

                                    <p className='mt-2 flex-1 text-[15px] leading-relaxed text-muted-foreground'>
                                        {item.description}
                                    </p>

                                    <BookButton
                                        selection={{ service: id }}
                                        variant='soft'
                                        className='mt-6 self-start'
                                    >
                                        Buat janji
                                        <ArrowRight className='size-4 transition-transform group-hover:translate-x-0.5' aria-hidden='true' />
                                    </BookButton>
                                </div>
                            </article>
                        )
                    })}
                </div>

                <h3 className='mt-16 text-lg font-bold text-ink'>
                    Layanan lainnya
                </h3>

                <div className='mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4'>
                    {otherServices.map(item => {
                        const Icon = item.icon

                        return (
                            <article
                                key={item.name}
                                className='flex flex-col rounded-2xl bg-white p-5 shadow-xs ring-1 ring-border transition-all duration-300 hover:shadow-md hover:ring-brand-200'
                            >
                                <span className='flex size-11 items-center justify-center rounded-xl bg-brand-50 text-brand-700'>
                                    <Icon className='size-5' aria-hidden='true' />
                                </span>

                                <h4 className='mt-4 font-bold text-ink'>
                                    {item.name}
                                </h4>

                                <p className='mt-1.5 text-sm leading-relaxed text-muted-foreground'>
                                    {item.description}
                                </p>
                            </article>
                        )
                    })}

                    <div className='relative isolate flex flex-col justify-between overflow-hidden rounded-2xl bg-ink p-5 text-white'>
                        <div className='absolute -top-10 -right-10 -z-10 size-32 rounded-full bg-brand-500/30 blur-2xl' aria-hidden='true' />

                        <div>
                            <h4 className='font-bold'>
                                Ada pertanyaan tentang layanan?
                            </h4>

                            <p className='mt-1.5 text-sm leading-relaxed text-white/75'>
                                Tanyakan jadwal, biaya, atau persiapan pemeriksaan lewat WhatsApp.
                            </p>
                        </div>

                        <a
                            href={whatsappLink('Halo Klinik Sehat Budi Luhur, saya ingin bertanya tentang layanan klinik.')}
                            target='_blank'
                            rel='noopener noreferrer'
                            className={cn(buttonVariants({ variant: 'inverse', size: 'sm' }), 'mt-5 self-start')}
                        >
                            <MessageCircle className='size-4 text-brand-600' aria-hidden='true' />
                            Chat WhatsApp
                        </a>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default ServicesSection
